/*
  tt-upcart-hooks.js — Tailgate Tikes UpCart integration hooks
  Dawn 15.5.0 migration, July 2026.

  Behavior-preserving vanilla rewrite of the inline jQuery script previously in
  layout/theme.liquid. jQuery has been removed from the theme; this file replaces
  the only two jQuery calls ($.load) with fetch + DOMParser, and the delegated
  jQuery click handler with a native one.

  What this does (unchanged from the original):
  1. licensedTag()      — strips the "Product Tag: " prefix from the licensed-product
                          line-item property inside the UpCart drawer and applies the
                          .license / .hide-product-tag classes so the officially-licensed
                          product info is styled, not shown raw.
  2. accordionDiscount() — wraps UpCart's discount-code form in an
                          "Apply a discount code" accordion.
  3. Express-checkout refresh on add-to-cart submit (StorefrontExpressButtons
                          re-init + refresh of the hidden additional_checkout_buttons
                          container UpCart pulls from).
  4. Header cart-icon count refresh, now via Dawn's Section Rendering API
                          (?sections=cart-icon-bubble) instead of re-downloading the
                          whole page — lighter, same result.
  5. Registers all window.upcart* lifecycle hooks.

  TT 2026-07-22 revision (property rename):
  The line-item property was renamed "Product Tag" -> "Licensing" in
  buy-buttons.liquid so Shopify's checkout (which renders properties verbatim,
  unstylable) shows presentable metadata. This file now matches BOTH prefixes
  during the transition; carts created before the rename carry the old name
  until the item is re-added. Safe to drop 'Product Tag:' from PREFIXES a few
  weeks after deploy.

  TT 2026-07-21 revision (licensed-tag pill showing raw "Product Tag:" text):
  a. licensedTag() is now MARKUP-AGNOSTIC — it locates the property by its text
     content ("Product Tag:") anywhere inside UpCart's container instead of
     relying on the .upcart-item-property strong selector, so UpCart drawer
     redesigns can't silently break it.
  b. The prefix is removed by direct TEXT REPLACEMENT in JS rather than relying
     on CSS to hide it — immune to stylesheet load order. The .license and
     .hide-product-tag classes are still applied for visual styling (UpCart
     custom CSS box + tt-base-custom.css keep working as before).
  c. API registration now RETRIES for up to ~10s waiting for UpCart's modern
     subscription API (upcartSubscribeCartUpdated) before falling back to the
     legacy window.upcart* assignments — fixes the race where this script
     evaluated before UpCart's bundle and permanently took the legacy branch.
  d. A debounced MutationObserver watches for UpCart drawer re-renders and
     re-runs licensedTag()/accordionDiscount() — safety net that keeps the
     cleanup applied even if no UpCart lifecycle event fires.

  Load from layout/theme.liquid before </body>:
    <script src="{{ 'tt-upcart-hooks.js' | asset_url }}" defer="defer"></script>
*/

(function () {
  var idle = window.requestIdleCallback || function (cb) { setTimeout(cb, 200); };

  idle(function () {
    function onReady(fn) {
      if (document.readyState !== 'loading') {
        fn();
      } else {
        document.addEventListener('DOMContentLoaded', fn);
      }
    }

    /* Fetch the current page and copy the innerHTML of `selector` from the fresh
       document into the matching element in the live document. Replaces
       jQuery's $(target).load(url + ' selector'). */
    function refreshFromPage(selector) {
      fetch(window.location.href, { headers: { Accept: 'text/html' } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var source = doc.querySelector(selector);
          var target = document.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        })
        .catch(function (err) {
          console.warn('TT: could not refresh ' + selector, err);
        });
    }

    /* Refresh the header cart icon + count bubble using Dawn's Section
       Rendering API — same mechanism Dawn's own cart.js uses. */
    function refreshCartIcon() {
      fetch(window.location.pathname + '?sections=cart-icon-bubble')
        .then(function (res) { return res.json(); })
        .then(function (sections) {
          var html = sections['cart-icon-bubble'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var source = doc.querySelector('.shopify-section');
          var target = document.getElementById('cart-icon-bubble');
          if (source && target) target.innerHTML = source.innerHTML;
        })
        .catch(function (err) {
          console.warn('TT: could not refresh cart icon', err);
        });
    }

    /* TT 2026-07-21: markup-agnostic licensed-tag cleanup.
       Finds any element inside an UpCart container whose direct text contains
       "Product Tag:", strips the prefix from the text itself, and applies the
       styling classes. Runs safely any number of times (per-element guard). */
    /* TT 2026-07-22: dual-prefix during the property rename (see header). */
    var PREFIXES = ['Licensing:', 'Product Tag:'];

    function matchPrefix(text) {
      for (var i = 0; i < PREFIXES.length; i++) {
        if (text.indexOf(PREFIXES[i]) !== -1) return PREFIXES[i];
      }
      return null;
    }

    function upcartRoots() {
      var roots = document.querySelectorAll('[id*="upcart" i], [class*="upcart" i]');
      return roots.length ? roots : [document.body];
    }

    function licensedTag() {
      upcartRoots().forEach(function (root) {
        // Walk only elements that could contain the property text.
        root.querySelectorAll('strong, span, p, div, dt, dd, small').forEach(function (el) {
          if (el.dataset.ttLicensed) return;
          if (el.children.length > 1) return; // want the leaf-ish node holding the text
          var text = el.textContent || '';
          var prefix = matchPrefix(text);
          if (!prefix) return;

          // Strip the prefix from the exact text node(s) so no CSS is needed to hide it.
          for (var i = 0; i < el.childNodes.length; i++) {
            var node = el.childNodes[i];
            if (node.nodeType === 3 && node.nodeValue.indexOf(prefix) !== -1) {
              node.nodeValue = node.nodeValue.replace(prefix, '').replace(/^\s+/, '');
            } else if (node.nodeType === 1 && node.textContent.indexOf(prefix) !== -1 && node.children.length === 0) {
              node.textContent = node.textContent.replace(prefix, '').replace(/^\s+/, '');
            }
          }

          el.dataset.ttLicensed = '1';
          el.classList.add('hide-product-tag');
          var parent = el.parentElement;
          if (parent) parent.classList.add('license');
        });
      });
    }

    function accordionDiscount() {
      if (document.querySelector('.accordion-header')) return;

      var module = document.querySelector('.upcart-discount-code-module');
      var form = module ? module.querySelector('form') : null;
      if (!module || !form) return;

      var accordion = document.createElement('div');
      accordion.className = 'accordion custom_discount_form';

      var header = document.createElement('div');
      header.className = 'accordion-header flex items-center justify-between cursor-pointer p-4 bg-gray-200 border-b';
      header.innerHTML = '<span class="header-text">Apply a discount code</span><span class="icon text-lg">+</span>';

      var content = document.createElement('div');
      content.className = 'accordion-content';
      content.style.display = 'none';
      content.appendChild(form);

      header.addEventListener('click', function () {
        content.style.display = content.style.display === 'none' ? 'block' : 'none';
        header.querySelector('.icon').textContent = content.style.display === 'none' ? '+' : '-';
      });

      accordion.appendChild(header);
      accordion.appendChild(content);
      module.appendChild(accordion);
    }

    function updateCart() {
      licensedTag();
      accordionDiscount();
      refreshCartIcon();
    }

    onReady(function () {
      /* Delegated click handler — replaces $(document).on('click', '.product-form__submit', ...) */
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.product-form__submit')) return;
        if (window.Shopify && window.Shopify.StorefrontExpressButtons) {
          window.Shopify.StorefrontExpressButtons.initialize();
        }
        refreshFromPage('.additional_checkout_buttons');
      });

      accordionDiscount(); // initial setup
      licensedTag();       // initial tag cleanup

      /* TT 2026-07-21: safety net — re-apply cleanup whenever UpCart re-renders
         its drawer contents, regardless of which lifecycle events fire.
         Debounced so rapid mutations trigger one pass. */
      var debounceTimer = null;
      var observer = new MutationObserver(function (mutations) {
        var relevant = false;
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.addedNodes.length) {
            for (var j = 0; j < m.addedNodes.length; j++) {
              var n = m.addedNodes[j];
              if (n.nodeType === 1 && matchPrefix(n.textContent || '')) { relevant = true; break; }
              if (n.nodeType === 1 && /upcart/i.test(n.className || '')) { relevant = true; break; }
            }
          }
          if (relevant) break;
        }
        if (!relevant) return;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          licensedTag();
          accordionDiscount();
        }, 100);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });

    /* UpCart lifecycle hooks.
       TT 2026-07-13: migrated to UpCart's modern Public API (official docs:
       docs.aftersell.com/upcart/common_upcart_api_uses). The legacy
       window.upcart* assignments are deprecated (console notice), and one —
       upcartRefreshCart — now COLLIDES with a function the modern API defines
       itself; assigning over it could break UpCart's own refresh. Modern
       subscriptions are used when available; the legacy assignments remain as
       an automatic fallback for older bundles. upcartValidateFormOnAddToCart
       had no modern equivalent and never actually validated (always returned
       undefined) — its work is folded into the AddedToCart subscription.

       TT 2026-07-21: registration now RETRIES while UpCart's bundle loads.
       Previously this checked for the modern API exactly once, and if this
       script won the race against UpCart's bundle it permanently fell back to
       the legacy assignments (which current bundles may never read). Now it
       polls for up to ~10s before falling back. */
    function registerModern() {
      window.upcartSubscribeCartUpdated(function () { updateCart(); });
      window.upcartSubscribeCartOpened(function () { updateCart(); });
      window.upcartSubscribeAddedToCart(function () {
        licensedTag();
        updateCart();
      });
    }

    function registerLegacy() {
      window.upcartOpenOnAddToCartOverride = function () { licensedTag(); };
      window.upcartRefreshCart = function () { licensedTag(); };
      window.upcartOnCartOpened = function () { updateCart(); };
      window.upcartOnCartUpdated = function () { updateCart(); };
      window.upcartValidateFormOnAddToCart = function () { updateCart(); };
    }

    var attempts = 0;
    (function tryRegister() {
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        registerModern();
        return;
      }
      attempts++;
      if (attempts < 40) {
        setTimeout(tryRegister, 250); // retry for ~10s
      } else {
        registerLegacy(); // UpCart bundle never exposed the modern API — fall back
      }
    })();
  });
})();